<template>
  <validation-provider
    :disabled="!validatable"
    :name="fieldName || label"
    :rules="validatable ? rules : ''"
    :vid="vid"
    v-slot="validationContext"
    slim
  >
    <b-form-group
      v-bind="$attrs"
      :label-cols-sm="noLabel ? undefined : labelColsSm"
      :state="getValidationState(validatable, validationContext)"
      :class="controlClass"
      :label-class="labelClass"
    >
      <template slot="label">
        <div>
          {{ noLabel ? undefined : fieldLabel }}
          <base-help-icon :help-description="helpDescription" />
        </div>
      </template>
      <slot v-bind:state="getValidationState(validatable, validationContext)">
        <treeselect
          name="name"
          :multiple="multiple"
          :clearable="clearable"
          :searchable="searchable"
          :disabled="disabled"
          :open-on-click="openOnClick"
          :open-on-focus="openOnFocus"
          :clear-on-select="clearOnSelect"
          :close-on-select="closeOnSelect"
          :always-open="alwaysOpen"
          :append-to-body="appendToBody"
          :options="options"
          :max-height="maxHeight"
          :placeholder="placeholder"
          :value="value"
          :load-options="loadOptions"
          :no-children-text="noChildrenText"
          @input="emitValue"
          :disable-branch-nodes="disableBranchNodes"
        />
      </slot>
      <b-form-invalid-feedback
        v-if="validatable"
        :state="getValidationState(validatable, validationContext)"
      >
        {{ validationContext.errors[0] }}
      </b-form-invalid-feedback>
    </b-form-group>
  </validation-provider>
</template>

<script>
import Treeselect from "@riophae/vue-treeselect";
import "@riophae/vue-treeselect/dist/vue-treeselect.css";
import { baseValidatableMixin } from "@foys-tech/foys-utils";

export default {
  components: { Treeselect },
  mixins: [baseValidatableMixin],
  props: {
    /**
     * Treeselect props
     * https://vue-treeselect.js.org/#props
     *
     * Json data structure:
     * {
     *  id: '<id>',
     * label: '<label>',
     * children: [
     *   {
     *     id: '<child id>',
     *     label: '<child label>',
     *   },
     *  ...
     *  ],
     * }
     *
     */

    /**
     * Generates a hidden <input /> tag with this field name for html forms.
     */
    name: String,

    /**
     * Set true to allow selecting multiple options (a.k.a., multi-select mode).
     */
    multiple: Boolean,

    /**
     * Whether to show an "×" button that resets value.
     */
    clearable: Boolean,

    /**
     * Whether to enable searching feature or not.
     */
    searchable: Boolean,

    /**
     * Whether to disable the control or not.
     */
    disabled: Boolean,

    /**
     * Whether to automatically open the menu when the control is clicked.
     */
    openOnClick: Boolean,

    /**
     * Whether to automatically open the menu when the control is focused.
     */
    openOnFocus: Boolean,

    /**
     * Whether to clear the search input after selecting an option.
     * Use only when :multiple="true".
     * For single-select mode, it always clears the input after selecting regardless of the prop value.
     */
    clearOnSelect: Boolean,

    /**
     * Whether to close the menu after selecting an option. Use only when :multiple="true".
     */
    closeOnSelect: Boolean,

    /**
     * Whether the menu should be always open.
     */
    alwaysOpen: Boolean,

    /**
     * Append the menu to <body />.
     */
    appendToBody: Boolean,

    /**
     * Array of available options. See here to learn how to define them.
     */
    options: Array,

    /**
     * Field placeholder, displayed when there's no value.
     */
    placeholder: String,

    /**
     * Used for dynamically loading options.
     * See here for detailed information.
     * Possible values of action: "LOAD_ROOT_OPTIONS", "LOAD_CHILDREN_OPTIONS" or "ASYNC_SEARCH".
     * callback - a function that accepts an optional error argument
     * parentNode - only presents when loading children options
     * searchQuery - only presents when searching async options
     * instanceId - eqauls to the value of instanceId prop you passed to vue-treeselect
     */
    loadOptions: Array,

    /**
     * Height size of the treeselect
     */
    maxHeight: Number,

    /**
     * Text displayed when a branch node has no children.
     */
    noChildrenText: String,

    /**
     * Whether to prevent branch nodes from being selected. See here for example.
     */
    disableBranchNodes: false,
  },
};
</script>
